<template>
  <div>
    <base-help-tooltip
      class="mb-5"
      popover-placement="top"
    >
      <template v-slot:left>
        <h3
          class="mb-0"
        >
          Add Trackers
        </h3>
      </template>
      <template v-slot:popover-body>
        <p
          class="mb-0"
        >
          We support the following pixel tracker domains:
        </p>
        <ul class="bg-light py-3">
          <li>Sizmek : https://bs.serving-sys.com</li>
          <li>Google Ad Manager : https://ad.doubleclick.net</li>
          <li>Atlas Solutions : https://ad.atdmt.com</li>
          <li>Flashtalking : http://servedby.flashtalking.com</li>
        </ul>
        <p
          class="mb-0"
        >
          Please use one of these for your impression and click tracking requirements.<br>

          We have no restrictions on post click trackers. Please add the string provided
          by your chosen tool and SmartMatch(TM) will append this to your Call To Action
          URL when your adverts are served.
        </p>
      </template>
    </base-help-tooltip>
    <b-form-group
      id="impression-tracker-input"
      class="mb-5"
    >
      <template
        v-slot:label
        label-for="impression-tracker-input"
      >
        <h4
          class="mb-1 form-label"
        >
          Impression Tracker
        </h4>
      </template>
      <b-form-textarea
        id="impression-tracker-input"
        :class="{ 'is-invalid': !isValidState('impressionTracker') }"
        :value="impressionTrackerValue"
        @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
        @blur="SET_IS_SIDEBAR_INPUT_FOCUSED(false)"
        @input="updateNewTrackers('impressionTracker', $event)"
        placeholder="Enter a URL"
        rows="5"
        max-rows="5"
        no-resize
        :disabled="!canUpdateTrackers"
        data-test="impression-tracker-input"
      />
      <p
        v-show="!isValidState('impressionTracker')"
        class="mb-0 text-danger"
      >
        <small>{{ trackers['impressionTracker'].error }}</small>
      </p>
    </b-form-group>
    <b-form-group
      id="click-tracker-input"
      class="mb-5"
    >
      <template
        v-slot:label
        label-for="click-tracker-input"
      >
        <h4
          class="mb-1 form-label"
        >
          Click Tracker
        </h4>
      </template>
      <b-form-textarea
        id="click-tracker-input"
        :class="{ 'is-invalid': !isValidState('clickTracker') }"
        :value="clickTrackerValue"
        @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
        @blur="SET_IS_SIDEBAR_INPUT_FOCUSED(false)"
        @input="updateNewTrackers('clickTracker', $event)"
        placeholder="Enter a URL"
        rows="5"
        max-rows="5"
        no-resize
        :disabled="!canUpdateTrackers"
        data-test="click-tracker-input"
      />
      <p
        v-show="!isValidState('clickTracker')"
        class="mb-0 text-danger"
      >
        <small>{{ trackers['clickTracker'].error }}</small>
      </p>
    </b-form-group>
    <b-form-group
      id="post-click-tracker-input"
      class="mb-5"
    >
      <template
        v-slot:label
        label-for="post-click-tracker-input"
      >
        <h4
          class="mb-1 form-label"
        >
          Post-Click Tracker
        </h4>
      </template>
      <b-form-textarea
        id="post-click-tracker-input"
        :class="{ 'is-invalid': !isValidState('postClickTracker') }"
        :value="postClickTrackerValue"
        @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
        @blur="SET_IS_SIDEBAR_INPUT_FOCUSED(false)"
        @input="updateNewTrackers('postClickTracker', $event)"
        placeholder="Enter a Post-Click Tracker"
        rows="5"
        max-rows="5"
        no-resize
        :disabled="!canUpdateTrackers"
        data-test="post-click-tracker-input"
      />
      <p
        v-show="!isValidState('postClickTracker')"
        class="mb-0 text-danger"
      >
        <small>{{ trackers['postClickTracker'].error }}</small>
      </p>
    </b-form-group>
    <b-button
      data-test="tracker-save-btn"
      variant="primary"
      :class="{ 'btn-loading': isSaveBtnLoading }"
      @click="saveChanges()"
      :disabled="!canSaveChanges"
    >
      Save
    </b-button>
  </div>
</template>
<script>
/* eslint-disable no-await-in-loop */
import { mapState, mapGetters, mapActions } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as trackerTypes from '@/modules/trackers/store/types';
import * as contentTypes from '@/modules/content/store/types';

function initialData() {
  return {
    isRequestingData: false,
    isSaveBtnLoading: false,
    isDirty: false,
    trackers: {
      impressionTracker: {
        type: 'IMPRESSION',
        id: null,
        value: null,
      },
      clickTracker: {
        type: 'CLICK',
        id: null,
        value: null,
      },
      postClickTracker: {
        type: 'POST_CLICK',
        id: null,
        value: null,
      },
    },
  };
}
export default {
  name: 'TrackersProperties',
  data() {
    return initialData();
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
    ]),
    ...mapState('content', [
      'selectedArticleUuid',
      'selectedCreativeId',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_READ_ONLY,
      campaignTypes.GET_IS_CAMPAIGN_REVIEW,
    ]),
    selectedCreativeArticleCombo() {
      return `${this.selectedCreativeId}-${this.selectedArticleUuid}`;
    },
    impressionTrackerValue() {
      if (this.trackers.impressionTracker.hasOwnProperty('newValue')) return this.trackers.impressionTracker.newValue;
      return this.trackers.impressionTracker.value;
    },
    clickTrackerValue() {
      if (this.trackers.clickTracker.hasOwnProperty('newValue')) return this.trackers.clickTracker.newValue;
      return this.trackers.clickTracker.value;
    },
    postClickTrackerValue() {
      if (this.trackers.postClickTracker.hasOwnProperty('newValue')) return this.trackers.postClickTracker.newValue;
      return this.trackers.postClickTracker.value;
    },
    canUpdateTrackers() {
      return !this.GET_IS_READ_ONLY()
        && !this.GET_IS_CAMPAIGN_REVIEW
        && !this.isRequestingData;
    },
    canSaveChanges() {
      return !this.GET_IS_READ_ONLY()
        && !this.GET_IS_CAMPAIGN_REVIEW
        && this.canUpdateTrackers
        && this.isDirty
        && !this.isSaveBtnLoading;
    },
  },
  methods: {
    ...mapActions('content', [
      contentTypes.SET_IS_SIDEBAR_INPUT_FOCUSED,
    ]),
    ...mapActions('trackers', [
      trackerTypes.FETCH_SELF_SERVICE_CONTENT_TRACKERS,
      trackerTypes.MANUALLY_ENTER_TRACKERS,
      trackerTypes.MANUALLY_UPDATE_TRACKER,
      trackerTypes.DELETE_TRACKERS,
    ]),
    updateNewTrackers(trackerType, value) {
      this.$set(this.$data.trackers[trackerType], 'newValue', value);
      this.isDirty = true;
      if (this.trackers[trackerType].hasOwnProperty('error')) delete this.trackers[trackerType].error;
    },
    hasChanged(trackerType) {
      const tracker = this.trackers[trackerType];
      if (tracker.hasOwnProperty('newValue')) {
        if (!tracker.newValue && !tracker.value) return false;
        return tracker.newValue !== tracker.value;
      }
      return false;
    },
    async createTracker(trackerType, tracker) {
      const payload = {
        campaignId: this.campaign.id,
        trackerInfo: {
          trackers: [tracker.newValue],
          scope: 'CONTENT_ITEM',
          type: tracker.type,
          creativeId: this.selectedCreativeId,
          referenceId: this.selectedArticleUuid,
        },
      };
      return await this.MANUALLY_ENTER_TRACKERS(payload)
        .then((response) => {
          this.setUpdatedValues({
            trackerType,
            trackerId: response.trackers[0].trackerId,
            value: response.trackers[0].value,
          });
        })
        .catch(() => {
          this.$set(this.$data.trackers[trackerType], 'error', 'Unsupported Tracker');
        });
    },
    async updateTracker(trackerType, tracker) {
      const payload = {
        trackerId: tracker.id,
        trackerValue: tracker.newValue,
      };
      return await this.MANUALLY_UPDATE_TRACKER(payload)
        .then((response) => {
          this.setUpdatedValues({
            trackerType,
            trackerId: response.trackerId,
            value: response.value,
          });
        })
        .catch(() => {
          this.$set(this.$data.trackers[trackerType], 'error', 'Unsupported Tracker');
        });
    },
    async deleteTracker(trackerType, trackerId) {
      return await this.DELETE_TRACKERS([trackerId])
        .then(() => {
          this.setUpdatedValues({
            trackerType,
            trackerId: null,
            value: null,
          });
        });
    },
    async saveChanges() {
      if (!this.canSaveChanges) return;
      this.isSaveBtnLoading = true;
      this.isRequestingData = true;
      try {
        for (const trackerType in this.trackers) {
          if (!this.hasChanged(trackerType)) continue;
          const tracker = this.trackers[trackerType];
          if (!tracker.id) await this.createTracker(trackerType, tracker);
          else if (!tracker.newValue) await this.deleteTracker(trackerType, tracker.id);
          else await this.updateTracker(trackerType, tracker);
        }
      } finally {
        this.isSaveBtnLoading = false;
        this.isRequestingData = false;
        this.isDirty = false;
      }
    },
    setUpdatedValues(payload) {
      this.trackers[payload.trackerType].id = payload.trackerId;
      this.trackers[payload.trackerType].value = payload.value;
      delete this.trackers[payload.trackerType].newValue;
      if (this.trackers[payload.trackerType].hasOwnProperty('error')) delete this.trackers[payload.trackerType].error;
    },
    isValidState(trackerType) {
      return !this.trackers[trackerType].hasOwnProperty('error');
    },
    async fetchTrackers() {
      this.isDirty = false;
      this.isRequestingData = true;
      const { advertiserId } = this.$route.params;
      await this.FETCH_SELF_SERVICE_CONTENT_TRACKERS({ advertiserId, creativeId: this.selectedCreativeId })
        .then((trackers) => {
          if (trackers.length === 0) return;
          const trackersTypes = Object.keys(this.trackers);
          trackersTypes.forEach((type) => {
            if (!trackers[0][type]) return;
            this.trackers[type].id = trackers[0][type].id || null;
            this.trackers[type].value = trackers[0][type].value || null;
          });
        });
      this.isRequestingData = false;
    },
    resetData() {
      Object.assign(this.$data, initialData());
    },
  },
  mounted() {
    this.fetchTrackers();
  },
  watch: {
    selectedCreativeArticleCombo(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.resetData();
        this.fetchTrackers();
      }
    },
  },
};
</script>
